import styled from 'styled-components';
import { BUTTON_SELECTOR, Button, ButtonType, IconButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { ReportColumn } from '@ardoq/api-types';
import { isColumnWithCustomLabel, isReferenceColumn } from '../utils';
import { PropsWithChildren, useId, useState } from 'react';
import { getReferenceDirectionIcon } from './filterUtils';
import { Popover2025 } from '@ardoq/popovers';
import { useDropdownTriggerContainer } from '@ardoq/dropdown-2026';
import { ListPopover, ListPopoverOptionProps } from '@ardoq/table-filter-bar';
import type { AddFilterButtonLabels } from '../labels';

export type { AddFilterButtonLabels };

const RemoveButtonIcon = styled(IconButton)`
  min-height: unset;
`;

const REMOVE_FILTER_BUTTON_DEFAULT_LABEL = 'Remove filter';

const RemoveFilterButton = ({
  onClick,
  label = REMOVE_FILTER_BUTTON_DEFAULT_LABEL,
}: {
  onClick: VoidFunction;
  label?: string;
}) => (
  <RemoveButtonIcon label={label} iconName={IconName.CLOSE} onClick={onClick} />
);

export const SelectContainer = ({ children }: PropsWithChildren) => (
  <FlexBox
    flexDirection={'column'}
    gap={'small'}
    padding={'medium'}
    borderRadius={'r8'}
    backgroundColor={'bgSubtle'}
    borderColor={'borderAction'}
    borderWidth={1}
    role="group"
  >
    {children}
  </FlexBox>
);

export const ColumnFilterLabel = ({
  column,
  removeFilter,
  removeFilterLabel,
}: {
  column: ReportColumn;
  removeFilter?: (columnKey: string) => void;
  removeFilterLabel?: string;
}) => (
  <FlexBox justify={'space-between'} align={'center'} flex={1}>
    <FlexBox gap={'xsmall'} align={'center'}>
      {column.label}
      {isReferenceColumn(column) && (
        <Icon
          iconSize={IconSize.SMALL}
          iconName={
            !isColumnWithCustomLabel(column)
              ? getReferenceDirectionIcon(column.type)
              : undefined
          }
        />
      )}
    </FlexBox>
    {removeFilter && (
      <RemoveFilterButton
        onClick={() => removeFilter(column.key)}
        label={removeFilterLabel}
      />
    )}
  </FlexBox>
);

const DEFAULT_ADD_FILTER_BUTTON_LABELS = {
  buttonLabel: 'Add filter',
  searchPlaceholder: 'Search filter',
} satisfies Required<AddFilterButtonLabels>;

export const AddFilterButton = ({
  options,
  isDisabled,
  labels,
}: {
  options: ListPopoverOptionProps[];
  isDisabled?: boolean;
  labels?: AddFilterButtonLabels;
}) => {
  const mergedLabels = { ...DEFAULT_ADD_FILTER_BUTTON_LABELS, ...labels };
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const popoverId = useId();

  const buttonRef = useDropdownTriggerContainer<HTMLButtonElement>({
    isOpen,
    openDropdown: () => !isOpen && setIsOpen(true),
  });

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleClose = () => {
    setIsOpen(false);
    setSearchValue('');
  };

  return (
    <>
      <Button
        ref={buttonRef}
        buttonType={ButtonType.GHOST}
        onClick={() => setIsOpen(prev => !prev)}
        isDisabled={isDisabled}
      >
        <FlexBox gap="xsmall" align="center">
          <Icon iconName={IconName.ADD_CIRCLE} />
          {mergedLabels.buttonLabel}
        </FlexBox>
      </Button>
      <Popover2025
        id={popoverId}
        isOpen={isOpen}
        onClose={handleClose}
        onClickOutside={handleClose}
        anchorRef={buttonRef}
        placement={{ side: 'bottom', overflow: 'left' }}
      >
        <ListPopover
          type="list"
          filter={{
            placeholder: mergedLabels.searchPlaceholder,
            value: searchValue,
            onChange: setSearchValue,
          }}
          groups={[{ options: filteredOptions }]}
        />
      </Popover2025>
    </>
  );
};

const RemoveButtonContainer = styled.div`
  position: relative;

  ${BUTTON_SELECTOR} {
    position: absolute;
    right: 0;
    top: 0;
  }
`;

export const RemoveFilterButtonInTopRightCorner = ({
  onClick,
  label,
}: {
  onClick: VoidFunction;
  label?: string;
}) => (
  <RemoveButtonContainer>
    <RemoveFilterButton onClick={onClick} label={label} />
  </RemoveButtonContainer>
);
